import { ArrowLeft, ArrowRight, Plus, Minus, User, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function Step3WorkItems({ formData, updateForm, onNext, onPrev, user }) {
  const updateQuantity = (index, delta) => {
    const newWorks = formData.works.map((w, i) => {
      if (i === index) {
        const newQty = Math.max(0, w.quantity + delta);
        return { ...w, quantity: newQty };
      }
      return w;
    });
    updateForm({ works: newWorks });
  };

  const setQuantity = (index, value) => {
    const num = parseInt(value) || 0;
    const newWorks = formData.works.map((w, i) =>
      i === index ? { ...w, quantity: Math.max(0, num) } : w
    );
    updateForm({ works: newWorks });
  };

  const handleNext = () => {
    const hasWork = formData.works.some((w) => w.quantity > 0);
    if (!hasWork) {
      toast.error("Please add at least one work item with a quantity greater than 0");
      return;
    }
    onNext();
  };

  return (
    <div className="form-container max-w-3xl mx-auto" data-testid="step-3-form">
      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Add Work
      </h2>
      <p className="text-sm text-[#71717A] mb-8">
        Select the work completed and specify quantities.
      </p>

      {/* Technician Info */}
      <div className="border-2 border-[#D4A537] bg-[#F0EDE4] p-4 mb-8" data-testid="technician-info">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#D4A537] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Technician
        </p>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-[#D4A537] flex items-center justify-center border-2 border-[#0A0A0A]">
            <User className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="font-semibold text-[#0A0A0A]" data-testid="technician-name">{user?.name || "N/A"}</p>
            <div className="flex items-center gap-1 text-sm text-[#71717A]">
              <Building2 className="w-3.5 h-3.5" />
              <span data-testid="technician-company">{user?.company || "N/A"}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Work Items */}
      <div className="space-y-0" data-testid="work-items-list">
        <div className="grid grid-cols-12 gap-0 border-2 border-[#0A0A0A] bg-[#D4A537] px-4 py-3">
          <div className="col-span-1 text-white text-xs uppercase tracking-[0.15em] font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Qty
          </div>
          <div className="col-span-8 text-white text-xs uppercase tracking-[0.15em] font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Work Description
          </div>
          <div className="col-span-3 text-white text-xs uppercase tracking-[0.15em] font-bold text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Actions
          </div>
        </div>

        {formData.works.map((work, index) => (
          <div
            key={index}
            data-testid={`work-item-${index}`}
            className={`grid grid-cols-12 gap-0 border-x-2 border-b-2 border-[#0A0A0A] px-4 py-3 items-center ${
              index % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"
            }`}
          >
            <div className="col-span-1">
              <span
                data-testid={`work-item-${index}-quantity`}
                className="text-lg font-bold mono"
                style={{ fontFamily: 'IBM Plex Mono, monospace', color: work.quantity > 0 ? '#D4A537' : '#9CA3AF' }}
              >
                {work.quantity}
              </span>
            </div>
            <div className="col-span-8">
              <p className="text-sm text-[#0A0A0A]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {work.name}
              </p>
            </div>
            <div className="col-span-3 flex items-center justify-center gap-2">
              <button
                data-testid={`work-item-${index}-decrease`}
                onClick={() => updateQuantity(index, -1)}
                className="w-8 h-8 flex items-center justify-center border-2 border-[#0A0A0A] bg-white hover:bg-[#F3F4F6] transition-colors"
                disabled={work.quantity === 0}
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
              <input
                data-testid={`work-item-${index}-input`}
                type="number"
                min="0"
                value={work.quantity}
                onChange={(e) => setQuantity(index, e.target.value)}
                className="w-14 h-8 text-center border-2 border-[#0A0A0A] text-sm font-bold mono"
                style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              />
              <button
                data-testid={`work-item-${index}-increase`}
                onClick={() => updateQuantity(index, 1)}
                className="w-8 h-8 flex items-center justify-center border-2 border-[#0A0A0A] bg-white hover:bg-[#F3F4F6] transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 flex justify-between">
        <button data-testid="step-3-prev-button" onClick={onPrev} className="brutalist-btn-outline flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Previous
        </button>
        <button data-testid="step-3-next-button" onClick={handleNext} className="brutalist-btn flex items-center gap-2">
          Next <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
